import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Trash2, Plus, Send, MessageSquare, Webhook, Mail } from 'lucide-react';

/**
 * S17 — Alert Channels CRUD.
 *
 * Slack / Teams / Webhook / Email channels with per-channel severity floor
 * + category filter. Backend routes:
 *   GET    /api/v1/clusters/{cid}/alerts/channels
 *   POST   /api/v1/clusters/{cid}/alerts/channels
 *   DELETE /api/v1/clusters/{cid}/alerts/channels/{channel_id}
 *   POST   /api/v1/clusters/{cid}/alerts/channels/{channel_id}/test
 */

const KIND_ICONS = {
  slack:   { Icon: MessageSquare, color: 'text-purple-600' },
  teams:   { Icon: MessageSquare, color: 'text-blue-600' },
  webhook: { Icon: Webhook,       color: 'text-gray-600' },
  email:   { Icon: Mail,          color: 'text-orange-600' },
};

const SEVERITY = ['info', 'warn', 'critical'];
const CATEGORIES = ['resiliency', 'financial', 'infra'];

export default function AlertChannelsPanel({ clusterId }) {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(null);
  const [testing, setTesting] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/v1/clusters/${clusterId}/alerts/channels`);
      setChannels(r.data?.channels || []);
    } catch (err) {
      setError(err?.response?.data?.detail || String(err));
    } finally {
      setLoading(false);
    }
  }, [clusterId]);

  useEffect(() => { if (clusterId) load(); }, [clusterId, load]);

  const create = async (draft) => {
    setCreating('saving');
    try {
      await axios.post(`${API}/v1/clusters/${clusterId}/alerts/channels`, draft);
      await load();
      setCreating(null);
    } catch (err) {
      setError(err?.response?.data?.detail || String(err));
      setCreating(null);
    }
  };

  const remove = async (channelId) => {
    if (!window.confirm('Delete this channel?')) return;
    try {
      await axios.delete(`${API}/v1/clusters/${clusterId}/alerts/channels/${channelId}`);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || String(err));
    }
  };

  const sendTest = async (channelId) => {
    setTesting(channelId);
    try {
      await axios.post(`${API}/v1/clusters/${clusterId}/alerts/channels/${channelId}/test`);
    } catch (err) {
      setError(err?.response?.data?.detail || String(err));
    } finally {
      setTesting(null);
    }
  };

  const toggle = async (channelId, field, value) => {
    try {
      await axios.patch(
        `${API}/v1/clusters/${clusterId}/alerts/channels/${channelId}`,
        { [field]: value },
      );
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || String(err));
    }
  };

  if (loading) return <Card><CardContent>Loading channels…</CardContent></Card>;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Alert Channels</CardTitle>
          <Button size="sm" onClick={() => setCreating({})}>
            <Plus className="w-4 h-4 mr-1" /> Add channel
          </Button>
        </div>
        <div className="text-xs text-gray-500">
          Where resiliency + financial alerts are delivered. Channels with
          mismatched severity / category are silently filtered.
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {error && (
          <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">
            {error}
          </div>
        )}

        {creating && (
          <ChannelEditor
            initial={creating}
            saving={creating === 'saving'}
            onSubmit={create}
            onCancel={() => setCreating(null)}
          />
        )}

        {channels.length === 0 && !creating && (
          <div className="text-sm text-gray-400 italic text-center py-6 border border-dashed rounded">
            No alert channels configured.
          </div>
        )}

        {channels.map((c) => {
          const { Icon, color } = KIND_ICONS[c.kind] || KIND_ICONS.webhook;
          return (
            <div key={c.channel_id} className="border rounded p-3 bg-white">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Icon className={`w-4 h-4 ${color}`} />
                  <div className="font-medium">{c.name || c.channel_id}</div>
                  <Badge variant="outline" className="text-xs">{c.kind}</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={c.enabled}
                    onCheckedChange={(v) => toggle(c.channel_id, 'enabled', v)}
                  />
                  <Button
                    size="sm" variant="outline"
                    onClick={() => sendTest(c.channel_id)}
                    disabled={testing === c.channel_id}
                  >
                    <Send className="w-3 h-3 mr-1" />
                    {testing === c.channel_id ? 'Sending…' : 'Test'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(c.channel_id)}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                <div>
                  <span className="text-gray-500">Target: </span>
                  <span className="font-mono">{c.target?.slice(0, 60)}{c.target?.length > 60 && '…'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Severity ≥: </span>
                  <span className="font-medium">{c.severity_floor || 'info'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Categories: </span>
                  <span className="font-medium">
                    {(c.categories?.length ? c.categories.join(', ') : 'all')}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

function ChannelEditor({ initial, saving, onSubmit, onCancel }) {
  const [draft, setDraft] = useState({
    name: '', kind: 'slack', target: '',
    severity_floor: 'info', categories: [],
    enabled: true, ...((typeof initial === 'object' && initial) || {}),
  });

  return (
    <div className="border-2 border-blue-300 rounded p-4 bg-blue-50 space-y-3">
      <div className="text-sm font-semibold mb-1">New channel</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Name</Label>
          <Input
            placeholder="e.g. SRE Slack #alerts"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
        </div>
        <div>
          <Label className="text-xs">Kind</Label>
          <Select value={draft.kind} onValueChange={(v) => setDraft({ ...draft, kind: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="slack">Slack</SelectItem>
              <SelectItem value="teams">MS Teams</SelectItem>
              <SelectItem value="webhook">Webhook</SelectItem>
              <SelectItem value="email">Email</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs">
            {draft.kind === 'email' ? 'Email address' : 'Webhook URL'}
          </Label>
          <Input
            placeholder={draft.kind === 'email' ? 'oncall@example.com' : 'https://hooks.slack.com/services/...'}
            value={draft.target}
            onChange={(e) => setDraft({ ...draft, target: e.target.value })}
          />
        </div>
        <div>
          <Label className="text-xs">Minimum severity</Label>
          <Select
            value={draft.severity_floor}
            onValueChange={(v) => setDraft({ ...draft, severity_floor: v })}
          >
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {SEVERITY.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Categories (empty = all)</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {CATEGORIES.map((cat) => (
              <button
                type="button"
                key={cat}
                onClick={() => setDraft({
                  ...draft,
                  categories: draft.categories.includes(cat)
                    ? draft.categories.filter((c) => c !== cat)
                    : [...draft.categories, cat],
                })}
                className={`px-2 py-1 text-xs rounded border ${
                  draft.categories.includes(cat)
                    ? 'bg-blue-600 text-white border-blue-700'
                    : 'bg-white text-gray-700 border-gray-300'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button onClick={() => onSubmit(draft)} disabled={saving || !draft.target}>
          {saving ? 'Saving…' : 'Create'}
        </Button>
      </div>
    </div>
  );
}
